import { displayConstraintTitle } from '@/utils/constraintFormMapping';

export const ARCHIVE_ENTRY_TYPES = ['all', 'commitments', 'constraints', 'decisions', 'milestones'];

/** Mockup type filter tabs — decisions appear under "all" only. */
export const ARCHIVE_TYPE_FILTER_VALUES = ['all', 'commitments', 'constraints', 'milestones'];

export const ARCHIVE_PERIOD_OPTIONS = [
  { value: '3m', months: 3 },
  { value: '6m', months: 6 },
  { value: '12m', months: 12 },
  { value: 'all', months: null },
];

export function isTerminalCommitment(commitment) {
  return commitment?.status === 'done' || commitment?.status === 'not_done';
}

export function isTerminalConstraint(constraint) {
  return constraint?.status === 'resolved';
}

export function isTerminalMilestone(milestone) {
  return milestone?.status === 'done';
}

export function isTerminalDecision(decision) {
  return decision?.status === 'decided';
}

function resolveEntrySortDate({ entityType, item }) {
  if (entityType === 'commitments') {
    if (item.status === 'done' && item.doneDate) return item.doneDate;
    if (item.status === 'not_done') {
      return item.doneDate || item.date_updated || item.dueDate || item.date_created;
    }
    return item.dueDate || item.date_updated || item.date_created;
  }
  if (entityType === 'constraints') {
    return item.resolvedAt || item.date_updated || item.dueDate || item.date_created;
  }
  if (entityType === 'decisions') {
    return item.decidedAt || item.date_updated || item.dueDate || item.date_created;
  }
  if (entityType === 'milestones') {
    return item.actualDate || item.date_updated || item.dueDate || item.date_created;
  }
  return item.date_updated || item.date_created || item.dueDate;
}

export function buildArchivedEntries({
  commitments = [],
  constraints = [],
  decisions = [],
  milestones = [],
} = {}) {
  const entries = [];

  commitments.filter(isTerminalCommitment).forEach((item) => {
    entries.push({
      id: `commitment-${item.id}`,
      entityType: 'commitments',
      entityId: item.id,
      title: item.was,
      status: item.status,
      sortDate: resolveEntrySortDate({ entityType: 'commitments', item }),
      ownerUserId: item.ownerUserId,
      ownerDisplayName: item.ownerDisplayName || null,
      milestoneId: item.milestoneId,
      milestoneTitle: item.milestoneTitle || null,
      dueDate: item.dueDate,
      raw: item,
    });
  });

  constraints.filter(isTerminalConstraint).forEach((item) => {
    entries.push({
      id: `constraint-${item.id}`,
      entityType: 'constraints',
      entityId: item.id,
      title: displayConstraintTitle(item),
      status: item.status,
      sortDate: resolveEntrySortDate({ entityType: 'constraints', item }),
      ownerUserId: item.ownerUserId,
      ownerDisplayName: item.ownerDisplayName || null,
      category: item.category,
      type: item.type,
      milestoneId: item.milestoneId,
      milestoneTitle: item.milestoneTitle || null,
      dueDate: item.dueDate,
      resolvedAt: item.resolvedAt,
      raw: item,
    });
  });

  decisions.filter(isTerminalDecision).forEach((item) => {
    entries.push({
      id: `decision-${item.id}`,
      entityType: 'decisions',
      entityId: item.id,
      title: item.title,
      status: item.status,
      sortDate: resolveEntrySortDate({ entityType: 'decisions', item }),
      ownerUserId: item.decisionOwnerUserId,
      ownerDisplayName: item.decisionOwnerDisplayName || null,
      milestoneId: item.milestoneId,
      milestoneTitle: item.milestoneTitle || null,
      dueDate: item.dueDate,
      decidedAt: item.decidedAt,
      raw: item,
    });
  });

  milestones.filter(isTerminalMilestone).forEach((item) => {
    entries.push({
      id: `milestone-${item.id}`,
      entityType: 'milestones',
      entityId: item.id,
      title: item.title,
      status: item.status,
      sortDate: resolveEntrySortDate({ entityType: 'milestones', item }),
      ownerUserId: item.responsibleUserId,
      ownerDisplayName: item.responsibleDisplayName || null,
      dueDate: item.dueDate,
      actualDate: item.actualDate,
      raw: item,
    });
  });

  return entries.sort((left, right) => new Date(right.sortDate) - new Date(left.sortDate));
}

export function filterArchivedEntriesBySearch(entries, query) {
  const normalized = String(query || '').trim().toLowerCase();
  if (!normalized) return entries;
  return entries.filter((entry) => String(entry.title || '').toLowerCase().includes(normalized));
}

export function filterArchivedEntriesByType(entries, type) {
  if (!type || type === 'all') return entries;
  return entries.filter((entry) => entry.entityType === type);
}

export function filterArchivedEntriesByPeriod(entries, periodValue) {
  const option = ARCHIVE_PERIOD_OPTIONS.find((item) => item.value === periodValue);
  if (!option || option.months == null) return entries;

  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setMonth(cutoff.getMonth() - option.months);

  return entries.filter((entry) => {
    const sortDate = new Date(entry.sortDate);
    return !Number.isNaN(sortDate.getTime()) && sortDate >= cutoff;
  });
}

export function formatArchiveMonthLabel(dateValue, locale) {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale || undefined, { month: 'long', year: 'numeric' });
}

export function groupArchivedEntriesByMonth(entries, locale) {
  const groups = new Map();

  entries.forEach((entry) => {
    const date = new Date(entry.sortDate);
    if (Number.isNaN(date.getTime())) return;

    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: formatArchiveMonthLabel(date, locale),
        entries: [],
      });
    }
    groups.get(key).entries.push(entry);
  });

  return Array.from(groups.values()).sort((left, right) => right.key.localeCompare(left.key));
}

export function buildArchiveSummary({
  commitments = [],
  constraints = [],
  decisions = [],
  milestones = [],
} = {}) {
  const doneCommitments = commitments.filter((item) => item.status === 'done').length;
  const notDoneCommitments = commitments.filter((item) => item.status === 'not_done').length;
  const resolvedConstraints = constraints.filter((item) => item.status === 'resolved').length;
  const decidedDecisions = decisions.filter((item) => item.status === 'decided').length;
  const doneMilestones = milestones.filter((item) => item.status === 'done').length;

  return {
    commitmentsTotal: commitments.length,
    commitmentsDone: doneCommitments,
    commitmentsNotDone: notDoneCommitments,
    constraintsTotal: constraints.length,
    constraintsResolved: resolvedConstraints,
    decisionsTotal: decisions.length,
    decisionsDecided: decidedDecisions,
    milestonesTotal: milestones.length,
    milestonesDone: doneMilestones,
  };
}

export function filterProjectsBySearch(projects, query) {
  const normalized = String(query || '').trim().toLowerCase();
  if (!normalized) return projects;
  return projects.filter((project) => {
    const name = String(project.name || '').toLowerCase();
    const description = String(project.description || '').toLowerCase();
    return name.includes(normalized) || description.includes(normalized);
  });
}
